import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Modal,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Alert,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { toast } from "react-toastify";
import { Message } from "../../../types";
import { sendFeedback } from "../../../axios/feedback";
import { useMessageInteractionTracking } from "../../../hooks/useMessageInteractionTracking";
import {
  modalStyles,
  headerStyles,
  contentStyles,
  messageContentBoxStyles,
  messageContentTextStyles,
  formControlStyles,
  actionsBoxStyles,
  submitButtonStyles,
  selectStyles,
  menuPaperStyles,
  feedbackFieldsContainerStyles,
  labelStyles,
} from "./styles";

interface LabeledTextFieldProps {
  label: string;
  required?: boolean;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  sx?: object;
}

const LabeledTextField: React.FC<LabeledTextFieldProps> = ({
  label,
  required,
  value,
  onChange,
  placeholder,
  multiline,
  rows,
  sx,
}) => (
  <Box sx={{ mb: 2, ...sx }}>
    <FormLabel required={required} sx={labelStyles}>
      {label}
    </FormLabel>
    <TextField
      fullWidth
      multiline={multiline}
      rows={rows}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      variant="outlined"
      hiddenLabel
    />
  </Box>
);

interface LabeledSelectProps {
  label: string;
  required?: boolean;
  value: string | number;
  onChange: (e: { target: { value: unknown } }) => void;
  children: React.ReactNode;
  sx?: object | null;
  selectSx?: object | null;
  menuPaperSx?: React.CSSProperties | object;
  inputRef?: React.Ref<HTMLDivElement>;
}

const LabeledSelect: React.FC<LabeledSelectProps> = ({
  label,
  required,
  value,
  onChange,
  children,
  sx,
  selectSx,
  menuPaperSx,
  inputRef,
}) => {
  const theme = useTheme();
  return (
    <Box sx={{ mb: 2, ...sx }}>
      <FormLabel required={required} sx={labelStyles}>
        {label}
      </FormLabel>
      <FormControl fullWidth>
        <Select
          ref={inputRef}
          value={value}
          onChange={onChange}
          sx={selectSx ?? selectStyles(theme)}
          MenuProps={
            menuPaperSx
              ? { PaperProps: { sx: menuPaperSx } }
              : undefined
          }
        >
          {children}
        </Select>
      </FormControl>
    </Box>
  );
};

interface MessageFeedbackModalProps {
  open: boolean;
  onClose: () => void;
  message: Message;
  previousUserMessage?: Message;
  onFeedbackSubmitted?: () => void;
  text?: string;
  activeTabLabel?: string;
}

interface FormData {
  incorrect: {
    wrongInfo: string;
    correction: string;
    source: string;
  };
  incomplete: {
    missingInfo: string;
    priority: string;
    sourceToInclude: string;
  };
  unclear: {
    unclearPart: string;
    suggestedPhrase: string;
    preferredFormat: string;
  };
  irrelevant: {
    whyIrrelevant: string;
    shouldAddress: string;
  };
  "too-generic": {
    specificityNeeded: string;
    exampleAnswer: string;
  };
  good: {
    howToMakeMoreUseful: string;
  };
  excellent: {
    whatDidYouLike: string;
  };
}

const complaintReasons = [
  {
    value: "incorrect",
    label: "Incorrect - Contains wrong or misleading information",
  },
  { value: "incomplete", label: "Incomplete - Missing important information" },
  { value: "unclear", label: "Unclear - Confusing or hard to understand" },
  { value: "irrelevant", label: "Irrelevant - Does not answer the question" },
  { value: "too-generic", label: "Too Generic - Lacks specific details" },
  { value: "good", label: "Good - How to make it more useful" },
  { value: "excellent", label: "Excellent - What did you like" },
];

const getFilteredReasons = (rating: number) => {
  if (rating === 5) {
    return complaintReasons.filter((reason) => reason.value === "excellent");
  } else if (rating === 4) {
    return complaintReasons.filter((reason) => reason.value === "good");
  } else {
    return complaintReasons.filter((reason) =>
      [
        "incorrect",
        "incomplete",
        "unclear",
        "irrelevant",
        "too-generic",
      ].includes(reason.value),
    );
  }
};

const ratingOptions = [
  {
    value: 5,
    label: "5 – 🌟 Excellent (clear, accurate, complete, fully useful)",
  },
  { value: 4, label: "4 – 👍 Good (mostly correct and useful, minor issues)" },
  { value: 3, label: "3 – 😐 Fair (usable but needs improvement)" },
  { value: 2, label: "2 – 🙁 Poor (some value, but major problems)" },
  { value: 1, label: "1 – 👎 Very Poor (serious issues, barely useful)" },
  { value: 0, label: "0 – ❌ Unacceptable (completely wrong or unusable)" },
];

const formatTextForReport = (value: string) =>
  value.replace(/!\[(.*?)\]\((.*?)\)/g, "$1: $2");

export const MessageFeedbackModal: React.FC<MessageFeedbackModalProps> = ({
  open,
  onClose,
  message,
  previousUserMessage,
  onFeedbackSubmitted,
  text,
  activeTabLabel,
}) => {
  const theme = useTheme();
  const displayMessageText =
    message.type === "tabbed_message"
      ? text?.trim()
        ? formatTextForReport(text)
        : activeTabLabel || "No text content"
      : text || message.text || "";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { trackInteraction } = useMessageInteractionTracking(message);
  const [rating, setRating] = useState<number | "">("");
  const [selectedReason, setSelectedReason] = useState("");
  const [formData, setFormData] = useState<FormData>({
    incorrect: { wrongInfo: "", correction: "", source: "" },
    incomplete: { missingInfo: "", priority: "", sourceToInclude: "" },
    unclear: { unclearPart: "", suggestedPhrase: "", preferredFormat: "" },
    irrelevant: { whyIrrelevant: "", shouldAddress: "" },
    "too-generic": { specificityNeeded: "", exampleAnswer: "" },
    good: { howToMakeMoreUseful: "" },
    excellent: { whatDidYouLike: "" },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const firstInputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (rating === 4) {
      setSelectedReason("good");
    } else if (rating === 5) {
      setSelectedReason("excellent");
    } else if (typeof rating === "number") {
      const availableReasons = getFilteredReasons(rating);
      if (
        selectedReason &&
        !availableReasons.some((reason) => reason.value === selectedReason)
      ) {
        setSelectedReason("");
      }
    }
  }, [rating, selectedReason]);

  const updateFormData = (field: string, value: string) => {
    if (!selectedReason) return;

    setFormData((prev) => ({
      ...prev,
      [selectedReason]: {
        ...prev[selectedReason as keyof FormData],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    if (
      rating === "" ||
      typeof rating !== "number" ||
      rating < 0 ||
      rating > 5 ||
      !selectedReason
    ) {
      setError("Please select a rating before submitting your feedback");
      return;
    }

    const currentData = formData[selectedReason as keyof FormData];
    const hasRequiredFields = Object.values(currentData).some((value) =>
      value.trim(),
    );

    if (!hasRequiredFields) {
      setError("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const ratingLabel =
        ratingOptions.find((r) => r.value === rating)?.label ||
        `Rating: ${rating}`;
      const reasonLabel =
        complaintReasons.find((r) => r.value === selectedReason)?.label ||
        selectedReason;

      let feedbackDetails: any = {};

      if (selectedReason === "incorrect") {
        const data = formData.incorrect;
        feedbackDetails = {
          wrong_info: data.wrongInfo,
          correction: data.correction,
          source: data.source,
        };
      } else if (selectedReason === "incomplete") {
        const data = formData.incomplete;
        feedbackDetails = {
          missing_info: data.missingInfo,
          priority: data.priority,
          source_to_include: data.sourceToInclude,
        };
      } else if (selectedReason === "unclear") {
        const data = formData.unclear;
        feedbackDetails = {
          unclear_part: data.unclearPart,
          suggested_phrase: data.suggestedPhrase,
          preferred_format: data.preferredFormat,
        };
      } else if (selectedReason === "irrelevant") {
        const data = formData.irrelevant;
        feedbackDetails = {
          why_irrelevant: data.whyIrrelevant,
          should_address: data.shouldAddress,
        };
      } else if (selectedReason === "too-generic") {
        const data = formData["too-generic"];
        feedbackDetails = {
          specificity_needed: data.specificityNeeded,
          example_answer: data.exampleAnswer,
        };
      } else if (selectedReason === "good") {
        const data = formData.good;
        feedbackDetails = {
          how_to_make_more_useful: data.howToMakeMoreUseful,
        };
      } else if (selectedReason === "excellent") {
        const data = formData.excellent;
        feedbackDetails = {
          what_did_you_like: data.whatDidYouLike,
        };
      }

      const quizDetails = message.buttons
        ? {
            buttons: message.buttons.flat().map((button) => ({
              text: button.text || "",
              isCorrect: button.isCorrect,
            })),
            total_buttons: message.buttons.flat().length,
            is_multiple_choice: message.buttons
              .flat()
              .some((btn) => "isCorrect" in btn),
            has_navigation: message.buttons
              .flat()
              .some((btn) =>
                ["Check Answer", "Next question", "Start"].includes(
                  btn.text || "",
                ),
              ),
          }
        : undefined;

      const feedbackData = {
        rating: rating as number,
        selected_stars: rating as number,
        rating_label: ratingLabel,
        reason: selectedReason,
        reason_label: reasonLabel,
        feedback_details: feedbackDetails,
        feedback_type: "message" as const,
        message_id: message.message_id,
        message_text: displayMessageText || text || message.text || "",
        message_type: message.type,
        domain: message.domain || "Unknown",
        focus: message.focus || "Unknown",
        user_id: message.user_id,
        date: message.date,
        quiz_details: quizDetails,
        full_message_data: JSON.stringify(message, null, 2),
        previous_user_message_data: previousUserMessage
          ? JSON.stringify(previousUserMessage, null, 2)
          : undefined,
      };

      await sendFeedback(feedbackData);

      trackInteraction("report", 0);

      toast.success("Report submitted successfully!", {
        position: "top-center",
        autoClose: 3000,
        closeOnClick: false,
        pauseOnHover: true,
      });

      if (onFeedbackSubmitted) {
        onFeedbackSubmitted();
      }

      handleClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to submit feedback";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setRating("");
    setSelectedReason("");
    setFormData({
      incorrect: { wrongInfo: "", correction: "", source: "" },
      incomplete: { missingInfo: "", priority: "", sourceToInclude: "" },
      unclear: { unclearPart: "", suggestedPhrase: "", preferredFormat: "" },
      irrelevant: { whyIrrelevant: "", shouldAddress: "" },
      "too-generic": { specificityNeeded: "", exampleAnswer: "" },
      good: { howToMakeMoreUseful: "" },
      excellent: { whatDidYouLike: "" },
    });
    setError(null);
    onClose();
  };

  const renderDynamicFields = () => {
    if (!selectedReason) return null;

    switch (selectedReason) {
      case "incorrect":
        const incorrectData = formData.incorrect;
        return (
          <Box sx={feedbackFieldsContainerStyles(theme)}>
            <LabeledTextField
              label="What is wrong?"
              required
              placeholder="Quote the exact sentence..."
              value={incorrectData.wrongInfo}
              onChange={(e) => updateFormData("wrongInfo", e.target.value)}
              multiline
              rows={isMobile ? 1 : 2}
            />
            <LabeledTextField
              label="Correction"
              required
              placeholder="Write the correct info..."
              value={incorrectData.correction}
              onChange={(e) => updateFormData("correction", e.target.value)}
              multiline
              rows={isMobile ? 1 : 2}
            />
            <LabeledTextField
              label="Source/Label/Guideline"
              placeholder="Name + link or citation..."
              value={incorrectData.source}
              onChange={(e) => updateFormData("source", e.target.value)}
            />
          </Box>
        );

      case "incomplete":
        const incompleteData = formData.incomplete;
        return (
          <Box sx={feedbackFieldsContainerStyles(theme)}>
            <LabeledTextField
              label="What's missing?"
              required
              placeholder={
                "• Missing point 1\n• Missing point 2\n• Missing point 3"
              }
              value={incompleteData.missingInfo}
              onChange={(e) => updateFormData("missingInfo", e.target.value)}
              multiline
              rows={isMobile ? 2 : 3}
            />
            <LabeledSelect
              label="Priority of missing info"
              required
              value={incompleteData.priority}
              onChange={(e) =>
                updateFormData("priority", String(e.target.value))
              }
              menuPaperSx={menuPaperStyles(theme) as React.CSSProperties}
            >
              <MenuItem value="Critical">Critical</MenuItem>
              <MenuItem value="Nice-to-have">Nice-to-have</MenuItem>
            </LabeledSelect>
            <LabeledTextField
              label="Source to include"
              placeholder="Name + link or citation..."
              value={incompleteData.sourceToInclude}
              onChange={(e) =>
                updateFormData("sourceToInclude", e.target.value)
              }
            />
          </Box>
        );

      case "unclear":
        const unclearData = formData.unclear;
        return (
          <Box sx={feedbackFieldsContainerStyles(theme)}>
            <LabeledTextField
              label="Which part is unclear?"
              required
              placeholder="Quote or describe..."
              value={unclearData.unclearPart}
              onChange={(e) => updateFormData("unclearPart", e.target.value)}
              multiline
              rows={isMobile ? 1 : 2}
            />
            <LabeledTextField
              label="How should it be phrased?"
              required
              placeholder="Suggest a clearer sentence..."
              value={unclearData.suggestedPhrase}
              onChange={(e) =>
                updateFormData("suggestedPhrase", e.target.value)
              }
              multiline
              rows={isMobile ? 1 : 2}
            />
            <LabeledSelect
              label="Preferred format"
              value={unclearData.preferredFormat}
              onChange={(e) =>
                updateFormData("preferredFormat", String(e.target.value))
              }
              menuPaperSx={menuPaperStyles(theme) as React.CSSProperties}
            >
              <MenuItem value="Bullets">Bullets</MenuItem>
              <MenuItem value="Step-by-step">Step-by-step</MenuItem>
              <MenuItem value="Table">Table</MenuItem>
            </LabeledSelect>
          </Box>
        );

      case "irrelevant":
        const irrelevantData = formData.irrelevant;
        return (
          <Box sx={feedbackFieldsContainerStyles(theme)}>
            <LabeledTextField
              label="Why irrelevant?"
              required
              placeholder="What it answered vs what you asked..."
              value={irrelevantData.whyIrrelevant}
              onChange={(e) => updateFormData("whyIrrelevant", e.target.value)}
              multiline
              rows={isMobile ? 1 : 2}
            />
            <LabeledTextField
              label="What should it address instead?"
              required
              placeholder="1–2 lines describing what should be addressed..."
              value={irrelevantData.shouldAddress}
              onChange={(e) => updateFormData("shouldAddress", e.target.value)}
              multiline
              rows={isMobile ? 1 : 2}
            />
          </Box>
        );

      case "too-generic":
        const genericData = formData["too-generic"];
        return (
          <Box sx={feedbackFieldsContainerStyles(theme)}>
            <LabeledTextField
              label="What specificity is needed?"
              required
              placeholder="e.g., product, line, country..."
              value={genericData.specificityNeeded}
              onChange={(e) =>
                updateFormData("specificityNeeded", e.target.value)
              }
              multiline
              rows={isMobile ? 1 : 2}
            />
            <LabeledTextField
              label="Example of an acceptable answer"
              required
              placeholder="1–2 lines with a specific example..."
              value={genericData.exampleAnswer}
              onChange={(e) => updateFormData("exampleAnswer", e.target.value)}
              multiline
              rows={isMobile ? 1 : 2}
            />
          </Box>
        );

      case "good":
        const goodData = formData.good;
        return (
          <Box sx={feedbackFieldsContainerStyles(theme)}>
            <LabeledTextField
              label="How to make it more useful to you (more clear, more accurate, more complete)?"
              required
              placeholder="Describe what would make this answer more helpful..."
              value={goodData.howToMakeMoreUseful}
              onChange={(e) =>
                updateFormData("howToMakeMoreUseful", e.target.value)
              }
              multiline
              rows={isMobile ? 2 : 3}
            />
          </Box>
        );

      case "excellent":
        const excellentData = formData.excellent;
        return (
          <Box sx={feedbackFieldsContainerStyles(theme)}>
            <LabeledTextField
              label="What did you like in it (clear, accurate, complete, fully useful)?"
              required
              placeholder="Describe what made this answer excellent..."
              value={excellentData.whatDidYouLike}
              onChange={(e) => updateFormData("whatDidYouLike", e.target.value)}
              multiline
              rows={isMobile ? 2 : 3}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  const isSubmitDisabled = () => {
    // Strict validation: rating must be a valid number (0-5)
    if (
      rating === "" ||
      typeof rating !== "number" ||
      rating < 0 ||
      rating > 5 ||
      !selectedReason ||
      isSubmitting
    ) {
      return true;
    }

    const currentData = formData[selectedReason as keyof FormData];

    // Check required fields based on feedback type
    switch (selectedReason) {
      case "incorrect":
        const incorrectData = currentData as FormData["incorrect"];
        return (
          !incorrectData.wrongInfo?.trim() || !incorrectData.correction?.trim()
        );

      case "incomplete":
        const incompleteData = currentData as FormData["incomplete"];
        return (
          !incompleteData.missingInfo?.trim() ||
          !incompleteData.priority?.trim()
        );

      case "unclear":
        const unclearData = currentData as FormData["unclear"];
        return (
          !unclearData.unclearPart?.trim() ||
          !unclearData.suggestedPhrase?.trim()
        );

      case "irrelevant":
        const irrelevantData = currentData as FormData["irrelevant"];
        return (
          !irrelevantData.whyIrrelevant?.trim() ||
          !irrelevantData.shouldAddress?.trim()
        );

      case "too-generic":
        const genericData = currentData as FormData["too-generic"];
        return (
          !genericData.specificityNeeded?.trim() ||
          !genericData.exampleAnswer?.trim()
        );

      case "good":
        const goodData = currentData as FormData["good"];
        return !goodData.howToMakeMoreUseful?.trim();

      case "excellent":
        const excellentData = currentData as FormData["excellent"];
        return !excellentData.whatDidYouLike?.trim();

      default:
        return true;
    }
  };

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        if (firstInputRef.current) {
          firstInputRef.current.focus();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="report-modal-title"
      aria-describedby="report-modal-description"
    >
      <Box
        sx={{
          ...modalStyles,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Box sx={headerStyles}>
          <Typography
            variant="h6"
            fontWeight={600}
            id="report-modal-title"
            color="text.primary"
          >
            Report Message Issue
          </Typography>
        </Box>

        <Box sx={contentStyles}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={messageContentBoxStyles(theme)}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{ mb: 1 }}
              color="text.primary"
            >
              Message Content:
            </Typography>
            <Typography variant="body2" sx={messageContentTextStyles(theme)}>
              {displayMessageText || "No text content"}
            </Typography>
          </Box>

          <LabeledSelect
            label="Rate the Answer (5–0)"
            value={rating}
            onChange={(e) => setRating(e.target.value as number)}
            sx={{ ...formControlStyles, mb: { xs: 2, sm: 3 } }}
            menuPaperSx={menuPaperStyles(theme) as React.CSSProperties}
            inputRef={firstInputRef}
          >
            {ratingOptions.map((option) => {
                const parts = option.label.split(" – ");
                const ratingText = parts[0];
                const descriptionText = parts[1];
                const [emoji, ...descriptionParts] = descriptionText.split(" ");
                const mainWord = descriptionParts[0];
                const restOfDescription = descriptionParts.slice(1).join(" ");

                return (
                  <MenuItem key={option.value} value={option.value}>
                    <Box component="span">
                      <Box component="span" sx={{ fontWeight: "bold" }}>
                        {ratingText} – {emoji} {mainWord}
                      </Box>
                      {restOfDescription && (
                        <Box component="span"> {restOfDescription}</Box>
                      )}
                    </Box>
                  </MenuItem>
                );
              })}
          </LabeledSelect>

          {rating !== "" && (
            <LabeledSelect
              label="Reason for reporting"
              value={selectedReason}
              onChange={(e) => setSelectedReason(e.target.value as string)}
              sx={formControlStyles}
              menuPaperSx={menuPaperStyles(theme) as React.CSSProperties}
            >
              {getFilteredReasons(
                  typeof rating === "number" ? rating : 0,
                ).map((reason) => (
                  <MenuItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </MenuItem>
                ))}
            </LabeledSelect>
          )}

          {renderDynamicFields()}
        </Box>

        <Box sx={actionsBoxStyles}>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitDisabled()}
            variant="contained"
            sx={submitButtonStyles(theme)}
          >
            {isSubmitting ? "Submitting..." : "Submit Report"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
